import { useCallback, useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_MARKET_QUOTE } from "../graphql/positions";

interface MarketQuoteData {
  marketQuote?: {
    __typename?: string;
    currentPrice?: number;
    hasMarketData: boolean;
    [key: string]: any;
  } | null;
}

interface UseMarketQuoteAutoFillOptions {
  symbol: string;
  open: boolean;
  enabled: boolean;
}

export interface MarketQuoteAutoFillResult {
  marketContext: MarketQuoteData["marketQuote"] | null;
  autoFillPrice: number | null;
  isPriceEdited: boolean;
  markPriceEdited: () => void;
  isLoading: boolean;
  error: Error | null;
  lastAutoFilledSymbol: string | null;
}

export default function useMarketQuoteAutoFill({
  symbol,
  open,
  enabled,
}: UseMarketQuoteAutoFillOptions): MarketQuoteAutoFillResult {
  const [marketContext, setMarketContext] = useState<
    MarketQuoteData["marketQuote"] | null
  >(null);
  const [autoFillPrice, setAutoFillPrice] = useState<number | null>(null);
  const [isPriceEdited, setIsPriceEdited] = useState(false);
  const [pendingSymbol, setPendingSymbol] = useState<string | null>(null);
  const lastRequestedSymbolRef = useRef<string | null>(null);
  const [lastAutoFilledSymbol, setLastAutoFilledSymbol] = useState<
    string | null
  >(null);

  const [getMarketQuote, { data, error, loading }] =
    useLazyQuery<MarketQuoteData>(GET_MARKET_QUOTE);

  const markPriceEdited = useCallback(() => setIsPriceEdited(true), []);
  const resetPriceEdited = useCallback(() => setIsPriceEdited(false), []);

  useEffect(() => {
    const trimmedSymbol = symbol?.trim() || "";
    if (!enabled || !open) {
      setMarketContext(null);
      setAutoFillPrice(null);
      setLastAutoFilledSymbol(null);
      setPendingSymbol(null);
      lastRequestedSymbolRef.current = null;
      resetPriceEdited();
      return;
    }

    if (!trimmedSymbol) {
      setMarketContext(null);
      setAutoFillPrice(null);
      setLastAutoFilledSymbol(null);
      setPendingSymbol(null);
      lastRequestedSymbolRef.current = null;
      resetPriceEdited();
      return;
    }

    if (trimmedSymbol === pendingSymbol) {
      return;
    }

    setPendingSymbol(trimmedSymbol);
    resetPriceEdited();
    lastRequestedSymbolRef.current = trimmedSymbol;
    getMarketQuote({ variables: { symbol: trimmedSymbol } });
  }, [enabled, open, symbol, pendingSymbol, getMarketQuote, resetPriceEdited]);

  useEffect(() => {
    if (data?.marketQuote) {
      const requestedSymbol = lastRequestedSymbolRef.current;
      const currentSymbol = symbol?.trim() || "";
      if (requestedSymbol && requestedSymbol !== currentSymbol) {
        return;
      }
      setMarketContext(data.marketQuote);
      if (data.marketQuote.currentPrice != null) {
        setAutoFillPrice(data.marketQuote.currentPrice);
        if (requestedSymbol) {
          setLastAutoFilledSymbol(requestedSymbol);
        }
      } else {
        setAutoFillPrice(null);
        setLastAutoFilledSymbol(null);
      }
    }
  }, [data, symbol]);

  useEffect(() => {
    if (error) {
      setMarketContext({ hasMarketData: false });
      setAutoFillPrice(null);
      setLastAutoFilledSymbol(null);
      setPendingSymbol(null);
      lastRequestedSymbolRef.current = null;
    }
  }, [error]);

  return {
    marketContext,
    autoFillPrice,
    isPriceEdited,
    markPriceEdited,
    isLoading: loading,
    error: error ?? null,
    lastAutoFilledSymbol,
  };
}
